import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Switch, Button, Input, Popconfirm, Empty, Spin, message, Divider, Tooltip } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { makePostCall, getUserDetails } from '../utils/helper';
import ShortcutBuilder from './ShortcutBuilder';
import { summarizeShortcut, SHORTCUTS_UPDATED_EVENT } from '../utils/shortcuts';

const emitUpdated = () => window.dispatchEvent(new Event(SHORTCUTS_UPDATED_EVENT));

const emptyDraft = { uf_id: null, name: '', filter_json: {} };

/**
 * Profile-menu dialog mirroring the reference PACS "Configure Your Favourite
 * ShortCuts" screen:
 *   1. Shortcuts Available — master shortcuts the user can enable/disable
 *   2. My Shortcuts        — the user's own shortcuts (edit / delete)
 *   3. Create Your Own     — name + filter builder
 */
const ShortcutSettingsModal = ({ visible, onCancel }) => {
  const userId = getUserDetails()?.username;
  const [loading, setLoading] = useState(false);
  const [masters, setMasters] = useState([]);
  const [mine, setMine] = useState([]);
  const [draft, setDraft] = useState(emptyDraft);
  const [savingDraft, setSavingDraft] = useState(false);

  const fetchShortcuts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await makePostCall('/get-my-shortcuts', { user_id: userId });
      setMasters(res.data?.data?.masters || []);
      setMine(res.data?.data?.mine || []);
    } catch (e) {
      message.error('Failed to load shortcuts');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (visible) {
      fetchShortcuts();
      setDraft(emptyDraft);
    }
  }, [visible, fetchShortcuts]);

  const handleToggleMaster = async (qf_id, enabled) => {
    // optimistic
    setMasters((prev) => prev.map((m) => (m.qf_id === qf_id ? { ...m, enabled } : m)));
    try {
      await makePostCall('/toggle-master-shortcut', { user_id: userId, qf_id, enabled });
      emitUpdated();
    } catch (e) {
      message.error('Failed to update');
      fetchShortcuts();
    }
  };

  const startEditMine = (record) => {
    let parsed = {};
    try {
      parsed = record.uf_filter_json ? JSON.parse(record.uf_filter_json) : {};
    } catch (e) {
      parsed = {};
    }
    setDraft({ uf_id: record.uf_id, name: record.uf_name || '', filter_json: parsed });
  };

  const handleDeleteMine = async (uf_id) => {
    try {
      await makePostCall('/delete-my-shortcut', { user_id: userId, filter_id: uf_id });
      message.success('Shortcut deleted');
      if (draft.uf_id === uf_id) setDraft(emptyDraft);
      fetchShortcuts();
      emitUpdated();
    } catch (e) {
      message.error('Failed to delete shortcut');
    }
  };

  const handleSaveDraft = async () => {
    if (!draft.name?.trim()) {
      message.warning('Please enter a shortcut name');
      return;
    }
    if (!draft.filter_json || Object.keys(draft.filter_json).length === 0) {
      message.warning('Please add at least one filter');
      return;
    }
    setSavingDraft(true);
    try {
      await makePostCall('/save-my-shortcut', {
        user_id: userId,
        filter_id: draft.uf_id || undefined,
        uf_filter_name: draft.name.trim(),
        filters: JSON.stringify(draft.filter_json),
      });
      message.success(draft.uf_id ? 'Shortcut updated' : 'Shortcut created');
      setDraft(emptyDraft);
      fetchShortcuts();
      emitUpdated();
    } catch (e) {
      message.error('Failed to save shortcut');
    } finally {
      setSavingDraft(false);
    }
  };

  const sectionTitle = (t) => (
    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>{t}</div>
  );

  const rowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    padding: '8px 10px',
    borderRadius: 8,
    border: '1px solid #EEF1F5',
    marginBottom: 6,
    background: '#FAFBFC',
  };

  return (
    <Modal
      title="Shortcut Settings"
      open={visible}
      onCancel={onCancel}
      footer={<Button onClick={onCancel}>Close</Button>}
      width={620}
      destroyOnClose
    >
      <Spin spinning={loading}>
        {/* 1. Master shortcuts available to enable */}
        {sectionTitle('Shortcuts Available')}
        {masters.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No shortcuts published yet" />
        ) : (
          masters.map((m) => (
            <div key={m.qf_id} style={rowStyle}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a' }}>{m.qf_name}</div>
                <div style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {summarizeShortcut(m.qf_filter_json)}
                </div>
              </div>
              <Switch
                size="small"
                checked={!!m.enabled}
                onChange={(checked) => handleToggleMaster(m.qf_id, checked)}
              />
            </div>
          ))
        )}

        <Divider style={{ margin: '16px 0' }} />

        {/* 2. The user's own shortcuts */}
        {sectionTitle('My Shortcuts')}
        {mine.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="You haven't created any shortcuts" />
        ) : (
          mine.map((u) => (
            <div key={u.uf_id} style={rowStyle}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#0f172a' }}>{u.uf_name}</div>
                <div style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {summarizeShortcut(u.uf_filter_json)}
                </div>
              </div>
              <div style={{ flexShrink: 0 }}>
                <Tooltip title="Edit">
                  <Button type="text" size="small" icon={<EditOutlined />} onClick={() => startEditMine(u)} />
                </Tooltip>
                <Popconfirm
                  title="Delete this shortcut?"
                  onConfirm={() => handleDeleteMine(u.uf_id)}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{ danger: true }}
                >
                  <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </div>
            </div>
          ))
        )}

        <Divider style={{ margin: '16px 0' }} />

        {/* 3. Create / edit own shortcut */}
        {sectionTitle(draft.uf_id ? 'Edit Shortcut' : 'Create Your Own Shortcut')}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>
            Shortcut Name
          </label>
          <Input
            placeholder="e.g. My CT pending"
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          />
        </div>

        <ShortcutBuilder
          value={draft.filter_json}
          onChange={(json) => setDraft((d) => ({ ...d, filter_json: json }))}
        />

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          {draft.uf_id && (
            <Button onClick={() => setDraft(emptyDraft)}>Cancel Edit</Button>
          )}
          <Button
            type="primary"
            icon={!draft.uf_id ? <PlusOutlined /> : undefined}
            loading={savingDraft}
            onClick={handleSaveDraft}
          >
            {draft.uf_id ? 'Save Changes' : 'Create Shortcut'}
          </Button>
        </div>
      </Spin>
    </Modal>
  );
};

export default ShortcutSettingsModal;
